import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, doc, updateDoc, deleteDoc,
  query, orderBy
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, Divider, Avatar,
  Alert, InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LagbasLogo from '../components/LagbasLogo';

// Statusflöde: kandidat → intervju → erbjudande → avtal → medarbetare
const STAGES = [
  { key: 'inkommen',    label: 'Inkommen',          color: '#757575', bg: '#f5f5f5' },
  { key: 'screening',  label: 'Screening',          color: '#1565C0', bg: '#e3f2fd' },
  { key: 'intervju1',  label: 'Intervju 1',         color: '#6a1b9a', bg: '#f3e5f5' },
  { key: 'intervju2',  label: 'Intervju 2',         color: '#4527a0', bg: '#ede7f6' },
  { key: 'erbjudande', label: 'Erbjudande',         color: '#E65100', bg: '#FFF3E0' },
  { key: 'avtal',      label: 'Avtal tecknat',      color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'avslutad',   label: 'Avslutad / Tackat nej', color: '#C62828', bg: '#FFEBEE' },
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  position: '',          // Sökt tjänst
  departmentId: '',
  stage: 'inkommen',
  source: '',            // Var hittades kandidaten (LinkedIn, spontanansökan, etc.)
  notes: '',
  cvUrl: '',
  appliedDate: '',
  interviewDate1: '',
  interviewDate2: '',
  offerDate: '',
  offerSalary: '',
  startDate: '',         // Planerat startdatum
  responsibleUid: '',    // Ansvarig rekryterare
  // Praktiska förutsättningar (behövs innan avtal)
  personnr: '',
  homeAddress: '',
  homeZip: '',
  homeCity: '',
  phoneChoice: '',       // 'iphone' | 'android'
  phoneNumbering: '',    // 'portera' | 'nytt'
};

export default function Recruitment() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const role = userProfile?.role || '';
  const canAccess = ['vd', 'superadmin', 'ao_ansvarig', 'gruppledare'].includes(role);

  const [candidates, setCandidates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    const [candSnap, deptSnap, userSnap] = await Promise.all([
      getDocs(query(collection(db, 'recruitment'), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, 'departments')),
      getDocs(collection(db, 'users')),
    ]);
    setCandidates(candSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleSave() {
    if (!form.firstName.trim() && !form.lastName.trim()) return;
    setSaving(true);
    try {
      const data = { ...form, updatedAt: new Date() };
      if (editingId) {
        await updateDoc(doc(db, 'recruitment', editingId), data);
        setCandidates(prev => prev.map(c => c.id === editingId ? { id: editingId, ...data } : c));
      } else {
        data.createdBy = currentUser.uid;
        data.createdAt = new Date();
        const ref = await addDoc(collection(db, 'recruitment'), data);
        setCandidates(prev => [{ id: ref.id, ...data }, ...prev]);
      }
      setDialog(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, e) {
    e.stopPropagation();
    if (!window.confirm('Ta bort kandidat?')) return;
    await deleteDoc(doc(db, 'recruitment', id));
    setCandidates(prev => prev.filter(c => c.id !== id));
  }

  async function handleStageChange(id, newStage) {
    await updateDoc(doc(db, 'recruitment', id), { stage: newStage, updatedAt: new Date() });
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: newStage } : c));
  }

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, responsibleUid: currentUser.uid });
    setDialog(true);
  }

  function openEdit(c, e) {
    e.stopPropagation();
    setEditingId(c.id);
    setForm({ ...EMPTY_FORM, ...c });
    setDialog(true);
  }

  const filtered = candidates.filter(c => {
    const q = search.toLowerCase();
    const name = `${c.firstName} ${c.lastName}`.toLowerCase();
    const matchSearch = !q || name.includes(q) || c.email?.toLowerCase().includes(q) || c.position?.toLowerCase().includes(q);
    const matchStage = !filterStage || c.stage === filterStage;
    const matchDept  = !filterDept  || c.departmentId === filterDept;
    return matchSearch && matchStage && matchDept;
  });

  // Gruppera per stage för kanban-vy
  const byStage = {};
  STAGES.forEach(s => { byStage[s.key] = filtered.filter(c => c.stage === s.key); });

  if (!loading && !canAccess) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Typography color="text.secondary">Åtkomst nekad – kontakta administratören.</Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Rekrytering</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {filtered.length} kandidater
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openNew}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
          Ny kandidat
        </Button>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* Filter row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Sök namn, e-post, tjänst…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200, bgcolor: 'white', borderRadius: 1 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )}} />
          <TextField select size="small" label="Status" value={filterStage}
            onChange={e => setFilterStage(e.target.value)}
            sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 1 }}>
            <MenuItem value="">Alla statusar</MenuItem>
            {STAGES.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Affärsområde" value={filterDept}
            onChange={e => setFilterDept(e.target.value)}
            sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 1 }}>
            <MenuItem value="">Alla AO</MenuItem>
            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Kanban-vy */}
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
              {STAGES.filter(s => s.key !== 'avslutad').map(stage => (
                <Box key={stage.key} sx={{ minWidth: 240, flex: '0 0 240px' }}>
                  {/* Kolumnrubrik */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Chip label={stage.label} size="small"
                      sx={{ bgcolor: stage.bg, color: stage.color, fontWeight: 700, fontSize: '0.72rem' }} />
                    <Typography variant="caption" color="text.disabled">
                      {byStage[stage.key]?.length || 0}
                    </Typography>
                  </Box>

                  {/* Kandidatkort */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(byStage[stage.key] || []).map(c => {
                      const initials = `${c.firstName?.[0] || ''}${c.lastName?.[0] || ''}`.toUpperCase() || '?';
                      const responsible = users.find(u => u.id === c.responsibleUid);
                      return (
                        <Paper key={c.id} sx={{ p: 1.5, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                          onClick={() => openEdit(c, { stopPropagation: () => {} })}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: stage.color, flexShrink: 0 }}>
                              {initials}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {c.firstName} {c.lastName}
                              </Typography>
                              {c.position && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {c.position}
                                </Typography>
                              )}
                            </Box>
                            <IconButton size="small" sx={{ p: 0.25 }}
                              onClick={e => handleDelete(c.id, e)}>
                              <DeleteIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            </IconButton>
                          </Box>
                          {c.email && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                              {c.email}
                            </Typography>
                          )}
                          {responsible && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.25 }}>
                              Ansvarig: {responsible.displayName || responsible.email}
                            </Typography>
                          )}
                          {/* Stegknappar */}
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                            {STAGES.filter(s => s.key !== stage.key).slice(0, 3).map(s => (
                              <Chip key={s.key} label={`→ ${s.label}`} size="small"
                                sx={{ fontSize: '0.6rem', height: 18, cursor: 'pointer',
                                  bgcolor: s.bg, color: s.color,
                                  '&:hover': { opacity: 0.8 } }}
                                onClick={e => { e.stopPropagation(); handleStageChange(c.id, s.key); }} />
                            ))}
                          </Box>
                          {/* Avtal-knapp om i avtal-fas */}
                          {c.stage === 'avtal' && (
                            <Button size="small" variant="outlined" startIcon={<GavelIcon />}
                              sx={{ mt: 1, fontSize: '0.65rem', py: 0.2, borderColor: '#FF6B00', color: '#FF6B00' }}
                              onClick={e => { e.stopPropagation(); navigate('/avtal'); }}>
                              Skapa anställningsavtal
                            </Button>
                          )}
                        </Paper>
                      );
                    })}
                    {(byStage[stage.key] || []).length === 0 && (
                      <Typography variant="caption" color="text.disabled"
                        sx={{ textAlign: 'center', display: 'block', py: 2, fontStyle: 'italic' }}>
                        Inga kandidater
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

              {/* Avslutade */}
              <Box sx={{ minWidth: 240, flex: '0 0 240px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Chip label="Avslutad / Tackat nej" size="small"
                    sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 700, fontSize: '0.72rem' }} />
                  <Typography variant="caption" color="text.disabled">
                    {byStage['avslutad']?.length || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(byStage['avslutad'] || []).map(c => (
                    <Paper key={c.id} sx={{ p: 1.5, opacity: 0.7, cursor: 'pointer' }}
                      onClick={() => openEdit(c, { stopPropagation: () => {} })}>
                      <Typography variant="body2" fontWeight={500}>
                        {c.firstName} {c.lastName}
                      </Typography>
                      {c.position && (
                        <Typography variant="caption" color="text.secondary">{c.position}</Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Statistikrad */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              {STAGES.map(s => (
                <Box key={s.key} sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: s.color }}>
                    {byStage[s.key]?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* Kandidatformulär */}
      <Dialog open={dialog} onClose={() => { setDialog(false); setEditingId(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
          {editingId ? 'Redigera kandidat' : 'Ny kandidat'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>

            {/* Personuppgifter */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f9f9f9', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Personuppgifter
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Förnamn" value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    sx={{ flex: '1 1 160px' }} size="small" />
                  <TextField label="Efternamn" value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    sx={{ flex: '1 1 160px' }} size="small" />
                  <TextField label="E-post" type="email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    sx={{ flex: '2 1 200px' }} size="small" />
                  <TextField label="Telefon" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    sx={{ flex: '1 1 140px' }} size="small" />
                </Box>
              </Box>
            </Grid>

            {/* Tjänst och status */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#fff8f0', borderRadius: 2, p: 2, border: '1px solid #FFD5B0' }}>
                <Typography variant="caption" fontWeight="bold" color="#E65100"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Tjänst och status
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Sökt tjänst / roll" value={form.position}
                    onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                    sx={{ flex: '2 1 200px' }} size="small" />
                  <TextField select label="Affärsområde" value={form.departmentId}
                    onChange={e => setForm(f => ({ ...f, departmentId: e.target.value }))}
                    sx={{ flex: '1 1 160px' }} size="small">
                    <MenuItem value=""><em>Välj AO</em></MenuItem>
                    {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                  </TextField>
                  <TextField select label="Status" value={form.stage}
                    onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}
                    sx={{ flex: '1 1 160px' }} size="small">
                    {STAGES.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
                  </TextField>
                  <TextField select label="Källa" value={form.source}
                    onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                    sx={{ flex: '1 1 160px' }} size="small">
                    <MenuItem value="">Välj källa</MenuItem>
                    <MenuItem value="linkedin">LinkedIn</MenuItem>
                    <MenuItem value="spontan">Spontanansökan</MenuItem>
                    <MenuItem value="annons">Platsannons</MenuItem>
                    <MenuItem value="rekommendation">Rekommendation</MenuItem>
                    <MenuItem value="headhunting">Headhunting</MenuItem>
                    <MenuItem value="ovrigt">Övrigt</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </Grid>

            {/* Datum */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f0faf4', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="#2E7D32"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Datum
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Ansökan inkommen" type="date" value={form.appliedDate}
                    onChange={e => setForm(f => ({ ...f, appliedDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} size="small" />
                  <TextField label="Intervju 1" type="date" value={form.interviewDate1}
                    onChange={e => setForm(f => ({ ...f, interviewDate1: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} size="small" />
                  <TextField label="Intervju 2" type="date" value={form.interviewDate2}
                    onChange={e => setForm(f => ({ ...f, interviewDate2: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} size="small" />
                  <TextField label="Erbjudande skickat" type="date" value={form.offerDate}
                    onChange={e => setForm(f => ({ ...f, offerDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} size="small" />
                  <TextField label="Planerat startdatum" type="date" value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} size="small" />
                </Box>
              </Box>
            </Grid>

            {/* Erbjudande och ansvarig */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField label="Erbjuden lön (kr/mån)" value={form.offerSalary}
                  onChange={e => setForm(f => ({ ...f, offerSalary: e.target.value }))}
                  sx={{ flex: '1 1 160px' }} size="small" />
                <TextField select label="Ansvarig rekryterare" value={form.responsibleUid}
                  onChange={e => setForm(f => ({ ...f, responsibleUid: e.target.value }))}
                  sx={{ flex: '2 1 200px' }} size="small">
                  <MenuItem value=""><em>Ingen</em></MenuItem>
                  {users
                    .filter(u => ['vd', 'superadmin', 'ao_ansvarig', 'gruppledare'].includes(u.role))
                    .map(u => <MenuItem key={u.id} value={u.id}>{u.displayName || u.email}</MenuItem>)}
                </TextField>
              </Box>
            </Grid>

            {/* Notering */}
            <Grid item xs={12}>
              <TextField fullWidth label="Notering / kommentar" multiline rows={3}
                placeholder="Intryck från intervju, styrkor, farhågor, nästa steg…"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>

            {/* Praktiska förutsättningar – behövs innan avtal */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f3e5f5', borderRadius: 2, p: 2, border: '1px solid #ce93d8' }}>
                <Typography variant="caption" fontWeight="bold" color="#6a1b9a"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Förutsättningar inför avtal
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField label="Personnummer" value={form.personnr}
                    onChange={e => setForm(f => ({ ...f, personnr: e.target.value }))}
                    placeholder="ÅÅMMDD-XXXX"
                    sx={{ flex: '1 1 160px' }} size="small" />
                  <TextField label="Hemadress (gatuadress)" value={form.homeAddress}
                    onChange={e => setForm(f => ({ ...f, homeAddress: e.target.value }))}
                    sx={{ flex: '2 1 220px' }} size="small" />
                  <TextField label="Postnummer" value={form.homeZip}
                    onChange={e => setForm(f => ({ ...f, homeZip: e.target.value }))}
                    sx={{ flex: '0 1 110px' }} size="small" />
                  <TextField label="Postort" value={form.homeCity}
                    onChange={e => setForm(f => ({ ...f, homeCity: e.target.value }))}
                    sx={{ flex: '1 1 140px' }} size="small" />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField select label="Telefon – önskad plattform" value={form.phoneChoice}
                    onChange={e => setForm(f => ({ ...f, phoneChoice: e.target.value }))}
                    sx={{ flex: '1 1 200px' }} size="small">
                    <MenuItem value=""><em>Ej valt</em></MenuItem>
                    <MenuItem value="iphone">iPhone</MenuItem>
                    <MenuItem value="android">Android</MenuItem>
                  </TextField>
                  <TextField select label="Mobilnummer" value={form.phoneNumbering}
                    onChange={e => setForm(f => ({ ...f, phoneNumbering: e.target.value }))}
                    sx={{ flex: '1 1 220px' }} size="small">
                    <MenuItem value=""><em>Ej valt</em></MenuItem>
                    <MenuItem value="portera">Portera befintligt nummer till Tikab</MenuItem>
                    <MenuItem value="nytt">Nytt nummer från Tikab</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </Grid>

            {/* Info om koppling till avtal */}
            {form.stage === 'avtal' && (
              <Grid item xs={12}>
                <Alert severity="success" icon={<GavelIcon />}>
                  <Typography variant="body2" fontWeight={600}>Klar för anställningsavtal</Typography>
                  <Typography variant="caption">
                    Kandidaten är redo att bli medarbetare. Gå till Avtalsregistret och skapa ett
                    anställningsavtal – välj denna person i "Välj person"-listan.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button onClick={() => { setDialog(false); setEditingId(null); }}>Avbryt</Button>
          {form.stage === 'avtal' && (
            <Button variant="outlined" startIcon={<GavelIcon />}
              onClick={() => { setDialog(false); navigate('/avtal'); }}
              sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
              Gå till avtalsregistret
            </Button>
          )}
          <Button variant="contained" size="large" onClick={handleSave}
            disabled={saving || (!form.firstName.trim() && !form.lastName.trim())}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Sparar…' : 'Spara kandidat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
